import { assertNever } from "@aistihealth/web-common/src/typings/typeUtils"
import * as luxon from "luxon"
import { DateTime } from "luxon"
import { useTranslation } from "next-i18next"
import { rem } from "polished"
import React, { FC, useCallback, useMemo } from "react"
import DayPickerInput from "react-day-picker/DayPickerInput"
import {
  DayPickerInputProps,
  DayPickerProps,
} from "react-day-picker/types/Props"
import styled, { css } from "styled-components"
import { theme } from "../../util/styles/theme"
import { Icon } from "../icon/Icon"
import { InputProps, InputSize } from "../inputs/base-input/BaseInput"
import {
  DatePickerContainer,
  DatePickerContainerProps,
} from "./DatePickerContainer"

interface IconSizing {
  fontSize: string
  height: string
}

const iconSizing: Record<InputSize, IconSizing> = {
  small: {
    fontSize: "1.5rem",
    height: "2rem",
  },
  medium: {
    fontSize: "2rem",
    height: "3rem",
  },
}

const IconContainer = styled.div<
  Pick<DatePickerContainerProps, "error" | "disabled" | "componentSize">
>`
  border-left: ${rem(1)} solid ${theme.colors.greyDark};
  display: flex;
  flex-direction: column;
  ${({ componentSize }) => {
    switch (componentSize) {
      case "small":
      case "medium":
        return css`
          font-size: ${iconSizing[componentSize].fontSize};
          height: ${iconSizing[componentSize].height};
        `
      default:
        return assertNever(componentSize)
    }
  }}
  justify-content: center;
  padding-left: ${theme.spacings.small};
  padding-right: ${theme.spacings.small};
  pointer-events: none;
  position: absolute;
  right: 0;
  top: 0;
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
`

export type DatePickerLocalizationProps = Pick<
  DayPickerProps,
  "firstDayOfWeek" | "locale" | "months" | "weekdaysLong" | "weekdaysShort"
>

export type DatePickerProps = {
  disabled?: boolean
  error?: boolean
  onChange?: (date: DateTime) => void
} & Pick<
  DayPickerInputProps,
  "format" | "placeholder" | "inputProps" | "value"
> & {
    dayPickerProps?: Omit<DayPickerProps, keyof DatePickerLocalizationProps>
  } & Pick<InputProps, "componentSize">

export const DatePicker: FC<DatePickerProps> = ({
  error,
  onChange,
  dayPickerProps,
  disabled,
  inputProps,
  componentSize,
  ...rest
}) => {
  const { i18n } = useTranslation()

  const datePickerLocalizationProps = useMemo<
    DatePickerLocalizationProps
  >(() => {
    const months = luxon.Info.months()

    // Luxon weekdays start from Monday and react-day-picker wants days starting from Sunday.
    // So convert Luxon weekday array to react-day-picker format
    const luxonWeekdaysLong = luxon.Info.weekdays("long")
    const luxonWeekdaysShort = luxon.Info.weekdays("short")

    const weekdaysLong = [
      luxonWeekdaysLong[6],
      ...luxonWeekdaysLong.slice(0, 6),
    ]
    const weekdaysShort = [
      luxonWeekdaysShort[6],
      ...luxonWeekdaysShort.slice(0, 6),
    ]

    const localizationProps: DatePickerLocalizationProps = {
      firstDayOfWeek: 1,
      locale: i18n.language,
      months,
      weekdaysLong,
      weekdaysShort,
    }

    return localizationProps
  }, [i18n.language])

  const formatDate = (date: Date, format: string, _locale: string) => {
    return DateTime.fromJSDate(date).toFormat(format)
  }
  const parseDate = (str: string, format: string, _locale: string) => {
    const date = DateTime.fromFormat(str, format)
    return date.isValid ? date.toJSDate() : undefined
  }

  const handleDayChange = useCallback(
    date => {
      onChange?.(date ? DateTime.fromJSDate(date) : undefined)
    },
    [onChange],
  )
  return (
    <DatePickerContainer
      error={error}
      disabled={disabled}
      componentSize={componentSize}
    >
      <DayPickerInput
        {...rest}
        dayPickerProps={{
          ...dayPickerProps,
          ...datePickerLocalizationProps,
        }}
        formatDate={formatDate}
        parseDate={parseDate}
        onDayChange={handleDayChange}
        inputProps={{ ...inputProps, disabled }}
      />
      <IconContainer
        error={error}
        disabled={disabled}
        componentSize={componentSize}
      >
        <Icon icon="calendar" />
      </IconContainer>
    </DatePickerContainer>
  )
}

DatePicker.defaultProps = {
  componentSize: "medium",
}
